// Google Analytics 4. O script só é carregado no domínio de produção;
// em desenvolvimento os eventos aparecem apenas no console.
//
// page_view é enviado manualmente (ver AnalyticsTracker) para sair com o título
// certo da página. Por isso, no GA4, a opção "Alterações na página de acordo com
// os eventos do histórico de navegação" da métrica otimizada fica DESLIGADA.

export const GA_MEASUREMENT_ID = "G-QMV47CP1W0";

const PRODUCTION_HOSTS = ["nutriclaracamille.com.au", "www.nutriclaracamille.com.au"];

// Ferramentas internas: não contam como visita.
const UNTRACKED_PATHS = ["/admin", "/forms"];

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const isProduction = () => PRODUCTION_HOSTS.includes(window.location.hostname);

const isUntrackedPath = (pathname: string) =>
  UNTRACKED_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

export const trackEvent = (name: string, params: Record<string, unknown> = {}) => {
  if (isUntrackedPath(window.location.pathname)) return;
  if (import.meta.env.DEV) console.debug("[analytics]", name, params);
  if (!isProduction()) return;
  window.gtag("event", name, params);
};

export const trackPageView = (pageLocation: string, pageTitle: string) => {
  trackEvent("page_view", { page_location: pageLocation, page_title: pageTitle });
};

// Classifica cliques que levam para fora do site (WhatsApp, Calendly, e-mail).
const trackContactLink = (url: string) => {
  if (/wa\.me|api\.whatsapp\.com/.test(url)) trackEvent("whatsapp_click", { link_url: url });
  else if (url.includes("calendly.com")) trackEvent("calendly_click", { link_url: url });
  else if (url.startsWith("mailto:")) trackEvent("email_click", { link_url: url });
};

const instrumentContactLinks = () => {
  // Links <a href="...">
  document.addEventListener(
    "click",
    (event) => {
      const anchor = (event.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (anchor) trackContactLink(anchor.href);
    },
    true,
  );

  // Botões que usam window.open(...) — a maioria dos CTAs do site.
  const originalOpen = window.open.bind(window);
  window.open = (url?: string | URL, target?: string, features?: string) => {
    if (url) trackContactLink(String(url));
    return originalOpen(url, target, features);
  };
};

// Agendamento concluído no pop-up do Calendly (postMessage do widget).
const listenCalendlyEvents = () => {
  window.addEventListener("message", (event) => {
    if (event.origin !== "https://calendly.com") return;
    const name = (event.data as { event?: string } | null)?.event;
    if (name === "calendly.date_and_time_selected") trackEvent("calendly_time_selected");
    if (name === "calendly.event_scheduled") trackEvent("generate_lead", { method: "calendly" });
  });
};

let initialized = false;

export const initAnalytics = () => {
  if (initialized) return;
  initialized = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // gtag.js exige o objeto `arguments`, não um array.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  instrumentContactLinks();
  listenCalendlyEvents();

  if (!isProduction()) return;

  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
};
